import React, { useState, useEffect } from 'react';
import { X, Loader2, Check, Package, AlertCircle, History, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import TranslationsEditor from './TranslationsEditor';
import { Input } from './ui/input';
import { vehiclesAPI, categoriesAPI, adminAPI, productsAPI } from '../services/api';
import { FitmentsEditor } from './FitmentsEditor';
import GalleryEditor from './GalleryEditor';
import RefListInput from './RefListInput';
import PackEditor from './PackEditor';
import PriceTierEditor from './PriceTierEditor';
import { productSeoScore, SeoGradeBadge, SeoScoreDetail } from './SubcategorySeoScore';
import { ExtraCategoriesField } from './ExtraCategoriesField';
import SubcategorySelect from './SubcategorySelect';
import { AutoCategoriesBlock } from './AutoCategoriesBlock';
import { buildMatrix, autoCategories, splitExtras } from '../services/categoryMatrix';
import ProductAiAssist from './ProductAiAssist';
import { ProductPublishReadiness } from './ProductPublishReadiness';
import PriceHistoryList from './PriceHistoryList';
import ProductSuppliersEditor from './ProductSuppliersEditor';
import CompetitorUrlsEditor from './CompetitorUrlsEditor';
import EquivalentsEditor from './EquivalentsEditor';
import TyreSpecsEditor from './TyreSpecsEditor';
import ProductSpecsEditor from './ProductSpecsEditor';
import ProductSeoEditor from './ProductSeoEditor';
import { toast } from 'sonner';
import { EMPTY, initialForm, buildPayload } from './product-form/productFormModel';
import ProductPricingFields from './product-form/ProductPricingFields';
import ProductCompatSection, { mergeRulesInto } from './product-form/ProductCompatSection';
import { FuelRestrictionField } from './FuelRestrictionField';

const ProductForm = ({ product = null, onClose, onSubmit, vatRate = 20, defaultMargin = 35, initialCategory = null, initialName = '' }) => {
  const isEdit = !!product;
  const [form, setForm] = useState(() => initialForm(product, initialName, initialCategory));
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [fitments, setFitments] = useState(null);

  const loadFitments = () => productsAPI.getFitments(product.id, true).then(d => setFitments(d.rows || [])).catch(() => setFitments([]));
  useEffect(() => {
    if (!product?.id) { setFitments([]); return; }
    loadFitments();
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const mergeRules = (newRules, mode) => setForm(f => mergeRulesInto(f, newRules, mode));

  useEffect(() => {
    vehiclesAPI.getBrands().then(setBrands).catch(() => setBrands([]));
    categoriesAPI.getAll().then(list => {
      setCategories(list);
      if (!product && list.length && !list.some(c => c.slug === EMPTY.category)) {
        setForm(f => ({ ...f, category: list[0].slug }));
      }
    }).catch(() => {});
  }, [product]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [draftInfo, setDraftInfo] = useState('');
  const draftDescription = async () => {
    setDrafting(true); setDraftError(''); setDraftInfo('');
    try {
      const res = await adminAPI.generateProductDescriptions([product.id], 1);
      const d = res.drafts?.[0];
      if (!d) { setDraftError('Aucun brouillon généré, réessayez'); return; }
      setForm(f => ({ ...f, description: d.description }));
      setDraftInfo('Brouillon IA inséré : relisez, ajustez si besoin, puis enregistrez.');
    } catch (e) {
      setDraftError(e.response?.data?.detail || 'Génération impossible pour le moment');
    } finally {
      setDrafting(false);
    }
  };
  const [improving, setImproving] = useState(false);
  const improveSeo = async () => {
    setImproving(true); setDraftError(''); setDraftInfo('');
    try {
      const r = await adminAPI.improveProductSeo(product.id);
      setForm(f => ({ ...f, description: r.description || f.description, name_i18n: { ...(f.name_i18n || {}), ...(r.name_i18n || {}) }, description_i18n: { ...(f.description_i18n || {}), ...(r.description_i18n || {}) } }));
      setDraftInfo(`Améliorations IA enregistrées : ${r.message}. Les autres critères (image, sous-catégorie, longueur du nom) restent à ajuster à la main.`);
    } catch (e) {
      setDraftError(e.response?.data?.detail || 'Amélioration impossible pour le moment');
    } finally {
      setImproving(false);
    }
  };
  const seo = productSeoScore(form);
  const setCategory = (e) => setForm(f => ({ ...f, category: e.target.value, subcategory: '' }));
  const matrix = React.useMemo(() => buildMatrix(categories), [categories]);
  // saved extras that the matrix explains are shown as automatic; the rest are the admin's manual rows
  const { manual: manualExtras } = React.useMemo(() => splitExtras(matrix, form.category, form.subcategory, form.extra_categories), [matrix, form.category, form.subcategory, form.extra_categories]);
  const autoExtras = autoCategories(matrix, form.category, [form.subcategory, ...manualExtras.map(x => x.subcategory)], manualExtras);
  const currentCategory = categories.find(c => c.slug === form.category);

  const handleSubmit = async () => {
    setError('');
    const r = buildPayload(form, { seo, fitments, manualExtras, autoExtras });
    if (r.error) { setError(r.error); return; }
    if (r.cancelled) return;
    setSaving(true);
    try {
      await onSubmit(r.payload);
      if (seo.grade === 'D' && r.payload.published) toast.warning(`Note SEO D (${seo.points}/${seo.max}) : la fiche est publiée mais mérite d'être retravaillée (voir le détail dans la fiche).`, { duration: 7000 });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="product-form">
        <div className="p-5 border-b bg-gradient-to-r from-orange-500 to-orange-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package size={22} />
            <h2 className="text-lg font-bold" data-testid="product-form-title">{isEdit ? `Modifier ${product.name}` : 'Ajouter un produit'}</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X size={22} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div data-testid="product-form-error" className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          <ProductAiAssist form={form} onDraft={(d) => setForm(f => ({
            ...f, name: d.name || f.name, brand: d.brand || f.brand, ref: d.ref || f.ref, type: d.type || f.type,
            category: d.category || f.category, subcategory: d.subcategory || '', description: d.description || f.description,
            oem_refs: d.oem_refs?.length ? d.oem_refs : f.oem_refs, refs: d.refs?.length ? d.refs : f.refs,
            attributes: Object.keys(d.attributes || {}).length ? { ...(f.attributes || {}), ...d.attributes } : f.attributes,
            compatibility: d.compatibility?.length ? d.compatibility : f.compatibility, ean: d.ean || f.ean,
          }))} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Nom du produit *</label>
              <Input data-testid="product-name-input" value={form.name} onChange={set('name')} placeholder="Plaquettes de frein" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Marque (fabricant) *</label>
              <Input data-testid="product-brand-input" value={form.brand} onChange={set('brand')} placeholder="BOSCH" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Référence principale *</label>
              <Input data-testid="product-ref-input" value={form.ref} onChange={set('ref')} placeholder="0 986 494 235" />
            </div>
            {product && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">URL SEO (slug) — figée pour préserver le référencement, vide = régénérer depuis nom-marque-référence</label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 whitespace-nowrap">/produit/</span>
                  <Input data-testid="product-slug-input" value={form.slug} onChange={set('slug')} placeholder="amortisseur-trw-jgt286t" className="font-mono text-sm" />
                </div>
              </div>
            )}
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer bg-gray-50 border rounded-lg px-3 py-2" data-testid="product-form-published-label">
                <input type="checkbox" checked={!!form.published} onChange={(e) => setForm(f => ({ ...f, published: e.target.checked }))} data-testid="product-form-published" className="rounded border-gray-300 text-orange-500" />
                <span className="font-medium text-gray-800">Publié</span> <span className="text-xs text-gray-500">— visible en boutique, dans la recherche et les flux. Décochez pour masquer (ex. prix non renseigné).</span>
              </label>
            </div>
            <ProductPublishReadiness score={seo} published={!!form.published} isNew={!product} onImprove={improveSeo} improving={improving || drafting} />
            <div className="md:col-span-2 flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid="product-seo-score">
              <SeoGradeBadge score={seo} size="lg" testId="product-seo-grade" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <p className="text-xs font-semibold text-gray-700">Qualité SEO de la fiche : {seo.points}/{seo.max} points · {seo.langs}/5 langues</p>
                  {product && (
                    <button type="button" data-testid="product-seo-improve" onClick={improveSeo} disabled={improving || drafting} className="inline-flex items-center gap-1 text-xs font-medium text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-60 rounded-md px-2.5 py-1">
                      {improving ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {improving ? 'Amélioration IA…' : 'Améliorer par IA (description + 4 langues)'}
                    </button>
                  )}
                </div>
                <SeoScoreDetail score={seo} testId="product-seo-checks" />
              </div>
            </div>
            <ProductSeoEditor productId={product?.id} seo={form.seo} onChange={(v) => setForm(f => ({ ...f, seo: v }))}
              onGenerated={(r) => setForm(f => ({ ...f, seo: r.seo, description: r.description || f.description, seo_i18n: r.seo_i18n || f.seo_i18n }))}
              onRestore={(sentence) => setForm(f => { const parts = (f.description || '').split(/\n\s*\n/); const i = parts.findIndex(x => x.trim() && !x.trim().startsWith('#')); if (i < 0) return { ...f, description: sentence }; parts[i] = `${parts[i].trim()} ${sentence}`; return { ...f, description: parts.join('\n\n') }; })} />
            <div className="md:col-span-2">
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className="block text-xs font-medium text-gray-600">Description <span className="font-normal text-gray-400">({(form.description || '').length} car. · affichée sur la fiche, utile pour Google)</span></label>
                {product && (
                  <button type="button" data-testid="product-description-ai" onClick={draftDescription} disabled={drafting} className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 hover:text-orange-800 disabled:opacity-50">
                    {drafting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {form.description ? 'Proposer une autre version (IA)' : 'Rédiger avec l\'IA'}
                  </button>
                )}
              </div>
              <textarea data-testid="product-description-input" value={form.description} onChange={set('description')} rows={10} maxLength={6000}
                placeholder="Introduction, puis sections « ## Caractéristiques techniques », « ## Compatibilité véhicule »… (Markdown léger : ## titre, - liste, paragraphes séparés par une ligne vide)"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              {draftError && <p className="text-xs text-red-600 mt-1" data-testid="product-description-ai-error">{draftError}</p>}
              {draftInfo && <p className="text-xs text-green-700 mt-1" data-testid="product-description-ai-info">{draftInfo}</p>}
            </div>
            <div className="md:col-span-2">
              <TranslationsEditor testId="product-translations" title="Nom et description dans les autres langues (fiche produit localisée)" context={`product page of the car part « ${form.name} » (brand ${form.brand}) — keep brand, reference and quantities in the name`}
                fields={[{ key: 'name', label: 'Nom', source: form.name, maxLength: 200 }, ...((form.description || '').trim() ? [{ key: 'description', label: 'Description', source: form.description, multiline: true, maxLength: 6000 }] : [])]}
                values={{ en: { name: form.name_i18n?.en, description: form.description_i18n?.en }, de: { name: form.name_i18n?.de, description: form.description_i18n?.de }, es: { name: form.name_i18n?.es, description: form.description_i18n?.es }, it: { name: form.name_i18n?.it, description: form.description_i18n?.it } }}
                onChange={(vals) => setForm(f => ({ ...f, name_i18n: Object.fromEntries(Object.entries(vals).map(([l, v]) => [l, v.name || ''])), description_i18n: Object.fromEntries(Object.entries(vals).map(([l, v]) => [l, v.description || ''])) }))} />
            </div>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50/40 border border-blue-100 rounded-lg">
              <RefListInput testId="product-refs" label="Références supplémentaires" hint="Anciennes références, références équivalentes du fabricant. Entrée pour ajouter (plusieurs séparées par des virgules)." placeholder="Ex : 0 986 424 797"
                values={form.refs} brands={form.ref_brands} exclude={[form.ref, ...form.oem_refs]} onChange={(refs, b) => setForm(f => ({ ...f, refs, ref_brands: b || f.ref_brands }))} />
              <RefListInput testId="product-oem-refs" label="Références OEM (constructeur)" hint="Numéros d'origine constructeur (ex : 1K0 615 301 AA pour Volkswagen). Trouvables via la recherche." placeholder="Ex : 1K0 615 301 AA"
                values={form.oem_refs} brands={form.ref_brands} exclude={[form.ref, ...form.refs]} onChange={(oem_refs, b) => setForm(f => ({ ...f, oem_refs, ref_brands: b || f.ref_brands }))} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Description / type</label>
              <Input data-testid="product-type-input" value={form.type} onChange={set('type')} placeholder="Essieu avant, Low-Metallic" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Catégorie *</label>
              <select data-testid="product-category-select" value={form.category} onChange={setCategory} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sous-catégorie</label>
              <SubcategorySelect testId="product-subcategory-select" category={currentCategory} value={form.subcategory} onChange={set('subcategory')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400" />
            </div>
            <AutoCategoriesBlock categories={categories} auto={autoExtras} />
            <ExtraCategoriesField categories={categories} primaryCategory={form.category} value={manualExtras} onChange={(v) => setForm(f => ({ ...f, extra_categories: v }))} />
            <ProductPricingFields form={form} set={set} setForm={setForm} vatRate={vatRate} defaultMargin={defaultMargin} productId={product?.id} />
          </div>

          <GalleryEditor images={form.images} onChange={(images) => setForm(f => ({ ...f, images }))} />
          <PackEditor packs={form.packs} unitPrice={form.price} onChange={(packs) => setForm(f => ({ ...f, packs }))} />
          <PriceTierEditor tiers={form.tiers} unitPrice={form.price} onChange={(tiers) => setForm(f => ({ ...f, tiers }))} />
          <ProductSuppliersEditor links={form.suppliers} warehouseStock={form.stock} onChange={(suppliers) => setForm(f => ({ ...f, suppliers }))} primaryId={form.primary_supplier_id} onPrimaryChange={(id) => setForm(f => ({ ...f, primary_supplier_id: id }))} />
          <ProductSpecsEditor form={form} setForm={setForm} category={currentCategory} onTemplateSaved={(updated) => setCategories(list => list.map(c => (c.id === updated.id ? updated : c)))} />
          <TyreSpecsEditor form={form} onChange={(patch) => setForm(f => ({ ...f, ...patch }))} />
          <EquivalentsEditor productId={product?.id} ids={form.equivalent_ids} currentBrand={form.brand.trim().toUpperCase()} onChange={(equivalent_ids) => setForm(f => ({ ...f, equivalent_ids }))} />
          <CompetitorUrlsEditor urls={form.competitor_urls} onChange={(competitor_urls) => setForm(f => ({ ...f, competitor_urls }))} autoAlign={form.auto_align} autoOffset={form.auto_align_offset} onAutoChange={(auto_align, auto_align_offset) => setForm(f => ({ ...f, auto_align, auto_align_offset }))} />

          {product?.id && (
            <details className="border rounded-lg" data-testid="product-history-section">
              <summary className="cursor-pointer px-4 py-3 font-semibold text-gray-900 flex items-center gap-2 select-none"><History size={18} className="text-orange-500" /> Historique des prix & stocks</summary>
              <div className="px-4 pb-4"><PriceHistoryList productId={product.id} limit={100} showProduct={false} /></div>
            </details>
          )}

          <FitmentsEditor fitments={fitments || []} onChange={setFitments} onRules={mergeRules} productInfo={{ product_id: product?.id || null, brand: form.brand, ref: form.ref, name: form.name, type: form.type || '', oem_refs: form.oem_refs || [], refs: form.refs || [] }} />

          <FuelRestrictionField value={form.fuel_types || []} onChange={(v) => setForm(f => ({ ...f, fuel_types: v }))} />

          <ProductCompatSection form={form} setForm={setForm} brands={brands} productId={product?.id} onFitmentsRefresh={loadFitments} />
        </div>

        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} data-testid="product-form-cancel">Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="product-form-submit">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />}
            {isEdit ? 'Enregistrer' : 'Créer le produit'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductForm;
